// Lightweight, dependency-free image-quality check run once on a captured
// frame (never on the live stream). It flags photos that are too blurry or
// poorly exposed so the inspector can retake before we spend an API call.
//
// Blur is estimated with the variance of the Laplacian: a sharp image has
// strong high-frequency edges (high variance); a blurry one is smooth (low
// variance). Brightness is the mean luminance. Both metrics are computed on a
// downscaled grayscale copy so the thresholds stay stable across devices.

import { loadImage } from './imageFile';

// --- Tunable thresholds (exported so they can be calibrated against real
//     aperture-screen photos without touching the algorithm). ---
export const QUALITY_DOWNSCALE_LONG_EDGE = 640; // px on the longest side
export const BLUR_VARIANCE_MIN = 120;           // below this => "blurry"
export const BRIGHTNESS_DARK_MAX = 45;          // mean luminance below => "dark"
export const BRIGHTNESS_BRIGHT_MIN = 215;       // mean luminance above => "bright"

export type QualityReason = 'blurry' | 'dark' | 'bright';

export type QualityResult = {
    pass: boolean;
    blurScore: number;    // variance of Laplacian (higher = sharper)
    brightness: number;   // mean luminance, 0-255
    reasons: QualityReason[];
};

/**
 * Assess a captured image. Accepts a data URL / object URL string or a Blob.
 * Resolves to a permissive "pass" if analysis can't run (e.g. canvas
 * unavailable), so the quality gate never hard-blocks a legitimate capture.
 */
export async function assessImageQuality(source: string | Blob): Promise<QualityResult> {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source);
    try {
        const img = await loadImage(url);
        const { data, width, height } = drawDownscaled(img);
        const gray = toGrayscale(data, width, height);
        const brightness = mean(gray);
        const blurScore = laplacianVariance(gray, width, height);

        const reasons: QualityReason[] = [];
        if (blurScore < BLUR_VARIANCE_MIN) reasons.push('blurry');
        if (brightness < BRIGHTNESS_DARK_MAX) reasons.push('dark');
        if (brightness > BRIGHTNESS_BRIGHT_MIN) reasons.push('bright');

        return { pass: reasons.length === 0, blurScore, brightness, reasons };
    } catch {
        // Analysis failed — don't stand in the inspector's way.
        return { pass: true, blurScore: NaN, brightness: NaN, reasons: [] };
    } finally {
        if (typeof source !== 'string') URL.revokeObjectURL(url);
    }
}

/** Human-readable message for a failed quality check. */
export function describeQuality(result: QualityResult): string {
    if (result.pass) return 'Looks good.';
    const parts: string[] = [];
    if (result.reasons.includes('blurry')) parts.push('a bit blurry');
    if (result.reasons.includes('dark')) parts.push('too dark');
    if (result.reasons.includes('bright')) parts.push('overexposed');
    return `This photo looks ${parts.join(' and ')}. Hold steady and retake for a clearer reading.`;
}

// --- internals ---

function drawDownscaled(img: HTMLImageElement): ImageData {
    const longEdge = Math.max(img.naturalWidth, img.naturalHeight) || 1;
    const scale = Math.min(1, QUALITY_DOWNSCALE_LONG_EDGE / longEdge);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
}

function toGrayscale(data: Uint8ClampedArray, width: number, height: number): Float32Array {
    const gray = new Float32Array(width * height);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        // Rec. 601 luma.
        gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return gray;
}

function mean(values: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < values.length; i++) sum += values[i];
    return values.length ? sum / values.length : 0;
}

// Variance of the response to the discrete Laplacian kernel
//   [ 0  1  0 ]
//   [ 1 -4  1 ]
//   [ 0  1  0 ]
function laplacianVariance(gray: Float32Array, width: number, height: number): number {
    if (width < 3 || height < 3) return Number.POSITIVE_INFINITY;
    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;
            const lap =
                gray[idx - width] +
                gray[idx + width] +
                gray[idx - 1] +
                gray[idx + 1] -
                4 * gray[idx];
            sum += lap;
            sumSq += lap * lap;
            n++;
        }
    }
    if (!n) return 0;
    const m = sum / n;
    return sumSq / n - m * m;
}
